import {BarEventStatus, UptimeBarPillToolTipProps} from "@uptime-com/uptimebar";
import classNames from "classnames";
import {ReactElement} from "react";

import {
  formatDateTime,
  inclusivePeriodEnd,
  isSameDayInZone,
  shortDurationFromInterval,
  shortFormatDateRange,
} from "@/lib/format.tsx";
import {DateFormatName, TimeFormatName} from "@/lib/types/common.ts";

import styles from "./UptimeBarPillToolTip.module.scss";

const barEventStatusTooltipStringMap: Partial<Record<BarEventStatus, string>> = {
  [BarEventStatus.DOWN]: "Down",
  [BarEventStatus.UP]: "Up",
  [BarEventStatus.NO_DATA]: "No Data",
  [BarEventStatus.MAINTENANCE]: "Maintenance",
};

type UptimeBarNewToolTipProps = UptimeBarPillToolTipProps & {
  timezone: string;
  currentDateFormat: DateFormatName;
  currentTimeFormat: TimeFormatName;
};

const UptimeBarNewToolTip = ({
  data,
  periodEnd,
  periodStart,
  timezone,
  currentDateFormat,
  currentTimeFormat,
}: UptimeBarNewToolTipProps) => {
  const displayEnd = inclusivePeriodEnd(periodEnd);
  const isSingleDay = isSameDayInZone(periodStart, displayEnd, timezone);
  let toolTipTimePeriod;
  if (isSingleDay) {
    toolTipTimePeriod = shortFormatDateRange(periodStart, displayEnd, timezone, currentDateFormat, currentTimeFormat);
  } else {
    toolTipTimePeriod = `${formatDateTime(periodStart, timezone, currentDateFormat, currentTimeFormat)} - ${formatDateTime(displayEnd, timezone, currentDateFormat, currentTimeFormat)}`;
  }
  const durationStr = shortDurationFromInterval(0, data.duration);

  return (
    <>
      <span className={"pulseTextBodyTiny pulse-color-white"}>{toolTipTimePeriod}</span>
      <span className={"pulseTextBodySmallBold pulse-color-white"}>
        <span className={classNames(styles.statusText, styles[data.status])}>
          {barEventStatusTooltipStringMap[data.status]}
        </span>{" "}
        for {durationStr}
      </span>
    </>
  );
};

export const UptimeBarNewToolTipRenderer = (
  timezone: string,
  currentDateFormat: DateFormatName,
  currentTimeFormat: TimeFormatName
): ((props: UptimeBarPillToolTipProps) => ReactElement<UptimeBarPillToolTipProps>) => {
  return ({data, periodEnd, periodStart}: UptimeBarPillToolTipProps): ReactElement<UptimeBarPillToolTipProps> => {
    return (
      <UptimeBarNewToolTip
        data={data}
        periodStart={periodStart}
        periodEnd={periodEnd}
        timezone={timezone}
        currentDateFormat={currentDateFormat}
        currentTimeFormat={currentTimeFormat}
      />
    );
  };
};
